import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import "./ViewRecords.css";

const ViewRecords = () => {
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecords = async () => {
      setIsLoading(true);
      setError(null);
      if (!patientUser || patientUser.role !== "patient") {
        setError("You are not authorized to view records. Please log in as a patient.");
        setIsLoading(false);
        return;
      }

      try {
        const [appointmentsResponse, prescriptionsResponse] = await Promise.all([
          api.get(`/appointments/patient/${patientUser.id}`, {
            headers: { Authorization: `Bearer ${patientUser.accessToken}` },
          }),
          api.get(`/appointments/patient/${patientUser.id}/prescriptions`, {
            headers: { Authorization: `Bearer ${patientUser.accessToken}` },
          }),
        ]);

        const appointments = Array.isArray(appointmentsResponse.data) ? appointmentsResponse.data : [];
        const prescriptionData = Array.isArray(prescriptionsResponse.data) ? prescriptionsResponse.data : [];

        const appointmentRecords = appointments
          .filter(app => app.records && app.records.length > 0)
          .flatMap(app => app.records.map(record => ({
            ...record,
            appointmentDate: app.date,
            appointmentId: app._id,
            type: "Other Record",
          })));

        const prescriptionRecords = prescriptionData.map(pres => ({
          filename: pres.pdfPath
            ? `Prescription-${new Date(pres.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf`
            : `Prescription-${pres._id}`,
          path: pres.pdfPath || null,
          uploadedAt: pres.createdAt,
          appointmentDate: pres.appointmentId ? pres.appointmentId.date : pres.createdAt,
          appointmentId: pres.appointmentId ? pres.appointmentId._id : null,
          type: "Prescription",
          prescriptionId: pres._id, // Add prescriptionId for direct download
        }));

        setRecords([...appointmentRecords, ...prescriptionRecords]);
        setPrescriptions(prescriptionData);
      } catch (error) {
        console.error("Error fetching records:", error.response?.data || error.message);
        setError("Failed to load records: " + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecords();
  }, [patientUser]);

  const handleDownload = async (record) => {
    if (!record.path) {
      setError("No file path available for download.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      if (record.type === "Prescription" && record.prescriptionId) {
        // Use the direct download endpoint for prescriptions
        const response = await api.get(`/appointments/prescriptions/${record.prescriptionId}/download-direct`, {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
          responseType: 'blob', // Handle binary data
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', record.filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Handle other records (e.g., appointment records) using existing logic
        let downloadUrl = record.path;
        if (record.path.includes('f005.backblazeb2.com')) {
          const fileName = record.path.split('/file/turemedicine-files/')[1];
          downloadUrl = `https://turemedicine-files.s3.us-east-005.backblazeb2.com/${fileName}`;
        }
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      setError("Failed to download file: " + (error.response?.data?.message || error.message));
      setTimeout(() => setError(null), 3000);
      console.error("Download error:", error);
    }
  };

  if (isLoading) return (
    <div className="view-records-container">
      <div className="loading">Loading records...</div>
    </div>
  );

  if (error) return (
    <div className="view-records-container">
      <div className="error-message">
        {error}
        <button onClick={() => navigate("/login?type=patient")}>Go to Login</button>
        <button onClick={() => navigate("/patient-dashboard")}>Back to Dashboard</button>
      </div>
    </div>
  );

  return (
    <div className="view-records-container">
      <h1>Your Records & Prescriptions</h1>

      <div className="records-section">
        <h2>Medical Records & Prescriptions</h2>
        {records.length > 0 ? (
          <div className="table-wrapper">
            <table className="records-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Appointment Date</th>
                  <th>File Name</th>
                  <th>Uploaded At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={index}>
                    <td>{record.type}</td>
                    <td>{new Date(record.appointmentDate).toLocaleString()}</td>
                    <td>{record.filename}</td>
                    <td>{new Date(record.uploadedAt).toLocaleString()}</td>
                    <td>
                      {record.path ? (
                        <button 
                          onClick={() => handleDownload(record)}
                          aria-label={`Download ${record.filename}`}
                        >
                          Download
                        </button>
                      ) : (
                        <span>No file</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No records or prescriptions found.</p>
        )}
      </div>

      <div className="prescriptions-section">
        <h2>Prescription Details</h2>
        {prescriptions.length > 0 ? (
          prescriptions.map((prescription, index) => (
            <div key={index} className="prescription-card">
              <p><strong>Date Issued:</strong> {new Date(prescription.createdAt).toLocaleString()}</p>
              <p><strong>Doctor:</strong> {prescription.doctorId?.name || "Unknown Doctor"}</p>
              <h3>Medications:</h3>
              <ul>
                {prescription.medications && prescription.medications.length > 0 ? (
                  prescription.medications.map((med, medIndex) => (
                    <li key={medIndex}>
                      {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                    </li>
                  ))
                ) : (
                  <li>No medications listed.</li>
                )}
              </ul>
              <p><strong>Instructions:</strong> {prescription.instructions || "N/A"}</p>
              {prescription.nextAppointmentDate && (
                <p><strong>Next Appointment:</strong> {new Date(prescription.nextAppointmentDate).toLocaleString()}</p>
              )}
              {prescription.pdfPath && (
                <button 
                  onClick={() => handleDownload({ type: "Prescription", path: prescription.pdfPath, prescriptionId: prescription._id, filename: `Prescription-${new Date(prescription.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf` })}
                  aria-label="Download prescription PDF"
                >
                  Download PDF
                </button>
              )}
            </div>
          ))
        ) : (
          <p>No detailed prescriptions found.</p>
        )}
      </div>
    </div>
  );
};

export default ViewRecords;